const productSubscriptionsV2 = class extends HTMLElement {
  constructor() {
    super()
    this._onVariantChange = (event) => {
      const form = this.closest('form')
      if (form && event.detail.formId && event.detail.formId !== form.id) { return }
      this.variantChanged(event.detail.variant)
    }
    this._onProductRerender = (event) => {
      const form = this.closest('form')
      if (!form || event.target !== form) { return }

      requestAnimationFrame(() => {
        const variantId = Number(form.querySelector('[name="id"]')?.value)
        if (!variantId || variantId === this.variantId) {
          this.syncHeaderPrice()
          return
        }

        this.variantId = variantId
        this.buildOptions()
        this.refreshSubscriptionDisplay()

        if (this.buyPrice) {
          const price = this.getCurrentVariantPrice()
          const compareAt = this.getCurrentVariantCompareAtPrice()
          this.currentVariant = { id: variantId, price, compare_at_price: compareAt }
          this.buyPrice.innerHTML = this.buildPrice(price, compareAt)
          this.updatePerServing(this.buyPerServing, price)
        }

        this.syncPurchaseType(this.activeType)
        this.syncHeaderPrice()
      })
    }
  }

  connectedCallback() {
    const form = this.closest('form')

    this.variantId = Number(this.dataset.selectedVariantId)
    this.subOptions = this.querySelectorAll('[data-subscription-option]')
    this.select = this.querySelector('[data-selling-plan-select]')
    this.activeType = [...this.subOptions].find(option => option.checked)?.value || 'subscribe'
    this.subContent = this.querySelector('[data-subscribe-content]')
    this.buyPrice = this.querySelector('[data-buy-price]')
    this.subPrice = this.querySelector('[data-sub-price]')
    this.buyPerServing = this.querySelector('[data-buy-per-serving]')
    this.subPerServing = this.querySelector('[data-sub-per-serving]')
    this.sellingPlanInput = form?.querySelector('[data-selling-plan-input]')
    this.quantityInput = form?.querySelector('[data-quantity-input]')
    this.quantityButtons = this.querySelectorAll('[data-quantity-option]')

    const optionsScript = this.querySelector('[data-options-json]')
    const servingsScript = this.querySelector('[data-variant-servings-json]')
    this.optionsJSON = optionsScript ? JSON.parse(optionsScript.textContent) : {}
    this.servingsJSON = servingsScript ? JSON.parse(servingsScript.textContent) : {}

    window.addEventListener('variant:change', this._onVariantChange)

    if (form) {
      form.addEventListener('product:rerender', this._onProductRerender)
      this._onFormSubmit = () => {
        this.syncPurchaseType(this.isOneTimeSelected() ? 'one-time' : 'subscribe')
      }
      form.addEventListener('submit', this._onFormSubmit, true)
    }

    this.subOptions.forEach((option) => {
      option.addEventListener('change', () => {
        this.updateType(option.value)
      })
    })

    if (this.select) {
      this.select.addEventListener('change', () => {
        this.updateSellingPlan()
      })
      this.select.addEventListener('mousedown', () => {
        this.selectOptionBox(this.select.closest('[data-subscription-box]'))
      })
    }

    this.quantityButtons.forEach((button) => {
      button.addEventListener('click', (event) => {
        event.preventDefault()
        event.stopPropagation()
        this.selectOptionBox(button.closest('[data-subscription-box]'))
        this.updateQuantity(button.dataset.quantityOption)
      })
    })

    this.bindPurchaseCtas()

    this.currentVariant = {
      id: this.variantId,
      price: this.getCurrentVariantPrice(),
      compare_at_price: this.getCurrentVariantCompareAtPrice()
    }

    this.syncPurchaseType(this.activeType)
    this.initPerServingPrices()
  }

  disconnectedCallback() {
    window.removeEventListener('variant:change', this._onVariantChange)
    const form = this.closest('form')
    form?.removeEventListener('product:rerender', this._onProductRerender)
    form?.removeEventListener('submit', this._onFormSubmit, true)
  }

  isOneTimeSelected() {
    return Boolean(this.querySelector('[data-subscription-option][value="one-time"]')?.checked)
  }

  syncPurchaseType(type) {
    this.activeType = type
    this.updatePurchaseUI()

    if (type === 'one-time') {
      this.disableSellingPlan()
    } else {
      this.resetQuantity()
      this.enableSellingPlan(this.select?.value)
    }

    this.syncHeaderPrice()
  }

  initPerServingPrices() {
    const variant = { id: this.variantId, price: this.getCurrentVariantPrice() }
    this.updatePerServing(this.buyPerServing, variant.price)

    if (this.select) {
      const selectedOption = this.select.querySelector('option:checked') || this.select.querySelector('option')
      if (selectedOption) {
        this.updatePerServing(this.subPerServing, Number(selectedOption.dataset.variantPrice))
      }
    }
  }

  getCurrentVariantPrice() {
    const priceEl = this.buyPrice?.querySelector('.product-subscriptions__option-price--bold')
    if (!priceEl) { return 0 }

    const match = priceEl.textContent.replace(/[^0-9.]/g, '')
    return Math.round(parseFloat(match) * 100)
  }

  getCurrentVariantCompareAtPrice() {
    const compareEl = this.buyPrice?.querySelector('s.text-subdued')
    if (!compareEl) { return null }

    const match = compareEl.textContent.replace(/[^0-9.]/g, '')
    const value = Math.round(parseFloat(match) * 100)
    return Number.isFinite(value) ? value : null
  }

  getHeaderPriceList() {
    const rerender = this.closest('product-rerender')
    if (rerender) {
      const quickBuyPrice = rerender.querySelector('.quick-buy-drawer__price price-list')
      if (quickBuyPrice) { return quickBuyPrice }
    }

    const scope = this.closest('.shopify-section--main-product-v2')
    return scope?.querySelector('.product-info-v2__price price-list') || null
  }

  syncHeaderPrice() {
    if (this.activeType === 'one-time') {
      const price = this.currentVariant?.price ?? this.getCurrentVariantPrice()
      const compareAt = this.currentVariant?.compare_at_price ?? this.getCurrentVariantCompareAtPrice()
      this.updateHeaderPrice(price, compareAt)
      return
    }

    const selectedOption = this.select?.querySelector('option:checked') || this.select?.querySelector('option')
    if (!selectedOption) { return }

    this.updateHeaderPrice(
      Number(selectedOption.dataset.variantPrice),
      Number(selectedOption.dataset.variantCompareAtPrice)
    )
  }

  updateHeaderPrice(price, comparePrice) {
    const priceList = this.getHeaderPriceList()
    const salePrice = priceList?.querySelector('sale-price')
    if (!salePrice || !price) { return }

    const srOnly = salePrice.querySelector('.sr-only')
    salePrice.replaceChildren(...(srOnly ? [srOnly] : []))
    salePrice.append(document.createTextNode(this.intToMoney(price)))

    const compareAt = priceList.querySelector('compare-at-price')
    const onSale = comparePrice && comparePrice > price

    if (onSale) {
      const compareEl = compareAt || document.createElement('compare-at-price')
      if (!compareAt) {
        compareEl.className = 'line-through'
        priceList.appendChild(compareEl)
      }

      const compareSrOnly = compareEl.querySelector('.sr-only')
      compareEl.replaceChildren(...(compareSrOnly ? [compareSrOnly] : []))
      compareEl.append(document.createTextNode(this.intToMoney(comparePrice)))
      salePrice.classList.add('bold')
    } else {
      compareAt?.remove()
      salePrice.classList.remove('bold')
    }
  }

  selectOptionBox(box) {
    if (!box) { return }

    const radio = box.querySelector('[data-subscription-option]')
    if (!radio || radio.checked) { return }

    radio.checked = true
    radio.dispatchEvent(new Event('change', { bubbles: true }))
  }

  bindPurchaseCtas() {
    const layout = this.closest('.product-subscriptions-v2__purchase-layout')
    if (!layout) { return }

    const oneTimeRadio = this.querySelector('[data-subscription-option][value="one-time"]')
    const oneTimeCta = layout.querySelector('.product-subscriptions-v2__cta--one-time')

    oneTimeCta?.addEventListener('mousedown', () => {
      if (oneTimeRadio && !oneTimeRadio.checked) {
        oneTimeRadio.checked = true
        oneTimeRadio.dispatchEvent(new Event('change', { bubbles: true }))
      }
    })
  }

  updatePurchaseUI() {
    const isOneTime = this.activeType === 'one-time'

    this.subOptions.forEach((option) => {
      option.closest('[data-subscription-box]')?.classList.toggle('is-active', option.checked)
    })

    if (this.subContent) {
      this.subContent.classList.toggle('is-inactive', isOneTime)
    }
  }

  updateType(value) {
    this.syncPurchaseType(value)
  }

  updateQuantity(value) {
    if (!this.quantityInput) { return }

    this.quantityInput.value = value
    this.quantityButtons.forEach((button) => {
      button.classList.toggle('is-selected', button.dataset.quantityOption === value)
    })
  }

  resetQuantity() {
    const minQty = this.quantityButtons[0]?.dataset.quantityOption || '1'
    this.updateQuantity(minQty)
  }

  disableSellingPlan() {
    const form = this.closest('form')
    if (!form) { return }

    // Prefer data attribute so we still find the field after removing its name.
    const planInputs = form.querySelectorAll(
      '[data-selling-plan-input], [name="selling_plan"], [name^="selling_plan_"]'
    )

    planInputs.forEach((input) => {
      input.disabled = true
      input.value = ''
      if (input.matches('[data-selling-plan-input], [name="selling_plan"]')) {
        input.removeAttribute('name')
      }
    })

    if (this.sellingPlanInput) {
      this.sellingPlanInput.disabled = true
      this.sellingPlanInput.value = ''
      this.sellingPlanInput.removeAttribute('name')
    }
  }

  enableSellingPlan(planId) {
    if (!planId) { return }

    const form = this.closest('form')
    if (!form) { return }

    const planInputs = this.sellingPlanInput
      ? [this.sellingPlanInput]
      : [...form.querySelectorAll('[data-selling-plan-input]')]

    planInputs.forEach((input) => {
      input.disabled = false
      input.setAttribute('name', 'selling_plan')
      input.value = planId
    })

    form.querySelectorAll('[name^="selling_plan_"]').forEach((input) => {
      const isSelectedPlan = input.name === `selling_plan_${planId}`
      input.disabled = !isSelectedPlan
      if (isSelectedPlan && !input.value) {
        input.value = 'subsave'
      }
    })
  }

  refreshSubscriptionDisplay() {
    if (!this.select) { return }

    const options = [...this.select.querySelectorAll('option')]
    const firstAvailableOption = options.find(option => !option.hidden && !option.disabled)
    if (!firstAvailableOption) { return }

    this.select.value = firstAvailableOption.value

    if (this.subPrice) {
      this.subPrice.innerHTML = this.buildPrice(
        Number(firstAvailableOption.dataset.variantPrice),
        Number(firstAvailableOption.dataset.variantCompareAtPrice)
      )
    }

    this.updatePerServing(this.subPerServing, Number(firstAvailableOption.dataset.variantPrice))
  }

  updateSellingPlan() {
    if (!this.select) { return }

    const selectedOption = [...this.select.querySelectorAll('option')].find(option => option.value === this.select.value)
    if (!selectedOption) { return }

    if (this.subPrice) {
      this.subPrice.innerHTML = this.buildPrice(
        Number(selectedOption.dataset.variantPrice),
        Number(selectedOption.dataset.variantCompareAtPrice)
      )
    }
    this.updatePerServing(this.subPerServing, Number(selectedOption.dataset.variantPrice))

    if (this.activeType === 'one-time') { return }

    this.enableSellingPlan(this.select.value)
    this.syncHeaderPrice()
  }

  variantChanged(variant) {
    this.variantId = variant.id
    this.currentVariant = variant
    this.buildOptions()

    if (this.buyPrice) {
      this.buyPrice.innerHTML = this.buildPrice(variant.price, variant.compare_at_price)
    }
    this.updatePerServing(this.buyPerServing, variant.price)
    this.refreshSubscriptionDisplay()
    this.syncPurchaseType(this.activeType)
  }

  buildOptions() {
    if (!this.select) { return }

    const data = this.optionsJSON[this.variantId] || this.optionsJSON[String(this.variantId)] || []
    const optionEls = data.map((option) => {
      return `
        <option
          value="${option.id}"
          data-variant-price="${option.price}"
          data-variant-compare-at-price="${option.compare_price}"
        >
          ${option.name}
        </option>
      `
    })

    this.select.innerHTML = optionEls.join('')
  }

  updatePerServing(element, price) {
    if (!element) { return }

    const servings = this.extractServings(
      this.servingsJSON[this.variantId] ?? this.servingsJSON[String(this.variantId)]
    )
    if (!servings) {
      element.hidden = true
      return
    }

    element.hidden = false
    element.textContent = `${this.intToMoney(price / servings)}/serving`
  }

  extractServings(value) {
    if (!value) { return null }

    const match = String(value).match(/(\d+(?:\.\d+)?)/)
    return match ? Number(match[1]) : null
  }

  buildPrice(price, comparePrice) {
    if (!comparePrice || price >= comparePrice) {
      return `
        <span class="product-subscriptions__option-price--bold">${this.intToMoney(price)}</span>
      `
    }

    return `
      <span class="product-subscriptions__option-price--bold">${this.intToMoney(price)}</span>
      <s class="text-subdued">${this.intToMoney(comparePrice)}</s>
    `
  }

  intToMoney(price) {
    return `$${(price / 100).toFixed(2)}`
  }
}

if (typeof window.customElements.get('product-subscriptions-v2') === 'undefined') {
  window.customElements.define('product-subscriptions-v2', productSubscriptionsV2)
}

function initProductV2QuantityButtons(root = document) {
  root.querySelectorAll('[data-quantity-buttons]').forEach((group) => {
    if (group.closest('product-subscriptions-v2')) { return }
    if (group.dataset.initialized === 'true') { return }
    group.dataset.initialized = 'true'

    const form = group.closest('form')
    const input = form?.querySelector('[data-quantity-input]') || group.parentElement?.querySelector('[data-quantity-input]')
    const buttons = group.querySelectorAll('[data-quantity-option]')

    buttons.forEach((button) => {
      button.addEventListener('click', () => {
        if (!input) { return }
        input.value = button.dataset.quantityOption
        buttons.forEach((item) => {
          item.classList.toggle('is-selected', item === button)
        })
      })
    })
  })
}

document.addEventListener('DOMContentLoaded', () => initProductV2QuantityButtons())
document.addEventListener('shopify:section:load', (event) => initProductV2QuantityButtons(event.target))
